import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {Detail, Icon, Text} from '@forecast-it/design-system';
import {FEATURE_FLAG_CARD_BORDER, FEATURE_FLAG_LINK_TEXT, FEATURE_FLAG_NEUTRAL_FILL} from '../featureFlagSurface';
import {clampTargetPercent} from './featureFlagRollout';

const Banner = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 12px 16px;
	background: ${FEATURE_FLAG_NEUTRAL_FILL};
	border-radius: 4px;
`;

const TargetBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-top: 20px;
`;

const TargetHeader = styled.div`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 12px;
`;

const MicroLabel = styled.span`
	> * {
		text-transform: uppercase;
		letter-spacing: 0.6px;
	}
`;

const TargetControls = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
`;

/*
 * The design system has no slider and no numeric stepper bounded the way this control needs, so both
 * this range input and the numeric field beside it are native inputs styled to sit next to the design
 * system's controls. They are the only hand-rolled primitives in this feature, accepted because the
 * screen is internal-only; anything else needed here should come from the design system.
 */
const TargetSlider = styled.input`
	flex: 1;
	height: 20px;
	accent-color: ${FEATURE_FLAG_LINK_TEXT};
`;

const NumericBlock = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	flex: none;
`;

const NumericInput = styled.input`
	width: 64px;
	height: 36px;
	padding: 0 10px;
	text-align: right;
	border: 1px solid ${FEATURE_FLAG_CARD_BORDER};
	border-radius: 4px;
`;

const InfoNote = styled.div`
	display: flex;
	gap: 10px;
	margin-top: 14px;
	padding: 12px 14px;
	background: ${FEATURE_FLAG_NEUTRAL_FILL};
	border-radius: 4px;
`;

const InfoIcon = styled.span`
	flex: none;
	margin-top: 1px;
`;

export interface RolloutPercentageTabProps {
	/** The flag's current percentage, as displayed — may read `<1%`. */
	currentPercentLabel: string;
	/** The numeric floor the target cannot go below. */
	currentPercent: number;
	/** e.g. `82 of 343 eligible companies`. */
	eligibleLabel: string;
	/**
	 * Eligible companies that already hold the flag through named users only, and so are never touched
	 * by a percentage fill — that only ever adds all-users grants and skips every company already on
	 * the flag. A rollout cannot reach 100% while this is non-zero; the target still accepts 100%, but
	 * saving it lands below that once these companies are excluded from the fill.
	 */
	namedUsersOnlyCount: number;
	targetPercent: number;
	/** Called with an already-clamped whole percentage. */
	onTargetPercentChange: (percent: number) => void;
}

export function RolloutPercentageTab({
	currentPercentLabel,
	currentPercent,
	eligibleLabel,
	namedUsersOnlyCount,
	targetPercent,
	onTargetPercentChange,
}: RolloutPercentageTabProps): JSX.Element {
	// The slider clamps on every drag tick — a drag always produces a whole, final value, so there is
	// nothing to protect. Clamping is what enforces the floor when the track is dragged below it.
	const handleSliderChange = (raw: string): void => {
		onTargetPercentChange(clampTargetPercent(Number.parseInt(raw, 10), currentPercent));
	};

	// The numeric field holds its own text while typing, separate from the committed target. Clamping
	// on every keystroke — as the slider does — makes the field unusable for typing anything below the
	// floor digit by digit: at a floor of 27%, typing "35" clamps the first "3" back to "27" before the
	// second digit is even entered, so the field can never reach "35". Typing is free-form here, and
	// only resolves into a real target on blur or Enter.
	const [numericText, setNumericText] = useState(String(targetPercent));
	useEffect(() => {
		setNumericText(String(targetPercent));
	}, [targetPercent]);

	// Resyncs the field to the clamped result directly, rather than relying on the `targetPercent` prop
	// round-trip: when a commit clamps back to the value the target already was (e.g. typing "5" at a
	// floor of 24%), the parent's state does not change, the resync effect above has nothing to react
	// to, and the field would otherwise keep showing the rejected "5" forever.
	const commitNumericText = (): void => {
		const clamped = clampTargetPercent(Number.parseInt(numericText, 10), currentPercent);
		setNumericText(String(clamped));
		onTargetPercentChange(clamped);
	};

	// Typed text that is ALREADY a valid target is committed as it is typed, so the target is live by
	// the time the operator reaches for Save. Waiting for blur would mean the first click on Save
	// lands on a still-disabled button — that click only commits the field, registering no press, so
	// nothing is saved and the operator has to click Save a second time.
	//
	// Only a value the clamp would leave untouched is committed here. Anything the clamp would move is
	// left alone until blur or Enter, because clamping mid-entry makes the field impossible to type
	// in: at a floor of 27%, "35" passes through "3", and committing a clamped "27" for that first
	// digit would overwrite the field before the second one arrives.
	const handleNumericChange = (raw: string): void => {
		setNumericText(raw);
		const parsed = Number.parseInt(raw, 10);
		if (clampTargetPercent(parsed, currentPercent) === parsed) {
			onTargetPercentChange(parsed);
		}
	};

	return (
		<div>
			<Banner>
				<Text>{`Currently at ${currentPercentLabel} of eligible companies`}</Text>
				<Detail color="medium">{eligibleLabel}</Detail>
			</Banner>
			<TargetBlock>
				<TargetHeader>
					<MicroLabel>
						<Detail size="2">ROLL OUT TO</Detail>
					</MicroLabel>
					<Detail color="medium">{`Locked below ${currentPercentLabel} — rollouts only go up`}</Detail>
				</TargetHeader>
				<TargetControls>
					<TargetSlider
						aria-label="Roll out to percent slider"
						max={100}
						min={0}
						onChange={event => handleSliderChange(event.target.value)}
						step={1}
						type="range"
						value={String(targetPercent)}
					/>
					<NumericBlock>
						<NumericInput
							aria-label="Roll out to percent"
							inputMode="numeric"
							max={100}
							min={currentPercent}
							onBlur={commitNumericText}
							onChange={event => handleNumericChange(event.target.value)}
							onKeyDown={event => {
								if (event.key === 'Enter') {
									commitNumericText();
								}
							}}
							type="number"
							value={numericText}
						/>
						<Detail color="medium">%</Detail>
					</NumericBlock>
				</TargetControls>
				<Detail color="medium">
					Companies are filled in a fixed order that is the same every time this opens, so raising the target only
					ever adds to what a lower target picked.
				</Detail>
			</TargetBlock>
			<InfoNote>
				<InfoIcon>
					<Icon icon="info" size="s" />
				</InfoIcon>
				<Detail color="medium">
					Trial and free-forever companies are not part of the eligible pool. Even at 100% the flag is not rolled out
					— new companies will not receive it until a developer removes the flag check in code.
				</Detail>
			</InfoNote>
			{namedUsersOnlyCount > 0 ? (
				<InfoNote>
					<InfoIcon>
						<Icon icon="info" size="s" />
					</InfoIcon>
					<Detail color="medium">
						{namedUsersOnlyCount === 1
							? '1 eligible company already holds this flag through named users only and is not touched by a percentage rollout — a 100% target will not fully reach it.'
							: `${namedUsersOnlyCount} eligible companies already hold this flag through named users only and are not touched by a percentage rollout — a 100% target will not fully reach them.`}
					</Detail>
				</InfoNote>
			) : null}
		</div>
	);
}
